// Admin Console — unified CRM + CMS hub at /admin (super-admins only).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, IndianRupee, Users, Inbox, TicketPercent, FolderKanban,
  Flower2, Loader2, ShieldAlert, ExternalLink, Settings,
} from "lucide-react";
import { UsersTab, SubmissionsTab, PromosTab } from "./AdminConsoleCrm";

const inr = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

const NAV = [
  { key: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { key: "revenue", label: "Revenue", Icon: IndianRupee },
  { key: "users", label: "Users / CRM", Icon: Users },
  { key: "submissions", label: "Submissions", Icon: Inbox },
  { key: "promos", label: "Promo Codes", Icon: TicketPercent },
  { key: "content", label: "Content / CMS", Icon: FolderKanban },
  { key: "settings", label: "Settings", Icon: Settings },
];

const CONTENT_LINKS = [
  { to: "/admin/quick-memorial", label: "Quick Add Memorial", desc: "Add memorials from Facebook & newspapers — name, photo/screenshot, dates, notes" },
  { to: "/admin/articles", label: "Articles", desc: "Write and manage site articles" },
  { to: "/admin/photos", label: "Photo Review", desc: "Review user-submitted photos" },
  { to: "/admin/ads", label: "Ads Manager", desc: "Manage ad placements" },
  { to: "/admin/moderation/feedback", label: "Moderation", desc: "Feedback & flagged content" },
  { to: "/celebrate/submit", label: "Memorial Wizard", desc: "The public 3-step Eternal Memorial flow" },
];

export default function AdminConsole() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [overview, setOverview] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!user) return;
    http.get("/admin/console/overview")
      .then((r) => setOverview(r.data))
      .catch((e) => setDenied(e?.response?.status === 403 || e?.response?.status === 401));
  }, [user]);

  if (!user || denied) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="admin-console-denied">
        <ShieldAlert className="w-8 h-8 mx-auto opacity-40" />
        <h1 className="font-display text-3xl mt-4">Admin access required</h1>
        <p className="text-sm opacity-70 mt-2">Sign in with a super-admin account to open the console.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-console">
      <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>INDIANFAMILYTREE.COM</div>
      <h1 className="font-display text-4xl mb-6">Admin Console</h1>
      <div className="flex gap-6 items-start flex-col md:flex-row">
        <aside className="w-full md:w-52 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible" data-testid="admin-sidebar">
          {NAV.map(({ key, label, Icon }) => (
            <button
              key={key} onClick={() => setTab(key)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition text-left"
              style={tab === key ? { background: "var(--wofy-primary)", color: "#fff" } : { color: "var(--wofy-text-soft)" }}
              data-testid={`admin-nav-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </aside>

        <main className="flex-1 min-w-0 w-full">
          {tab === "dashboard" && <DashboardTab overview={overview} goTo={setTab} />}
          {tab === "revenue" && <RevenueTab />}
          {tab === "users" && <UsersTab />}
          {tab === "submissions" && <SubmissionsTab />}
          {tab === "promos" && <PromosTab />}
          {tab === "content" && <ContentTab />}
          {tab === "settings" && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="wofy-card" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}

function DashboardTab({ overview, goTo }) {
  if (!overview) return <div className="wofy-card text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  return (
    <div className="space-y-4" data-testid="admin-dashboard-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Registered users" value={overview.users} testid="admin-stat-users" />
        <Stat label="Family trees" value={overview.trees} testid="admin-stat-trees" />
        <Stat label="Memorials" value={overview.memorials} testid="admin-stat-memorials" />
        <Stat label="Voice memories" value={overview.voice_memories} testid="admin-stat-voice" />
        <Stat label="Revenue (this month)" value={inr(overview.revenue_month_paise)} testid="admin-stat-month-revenue" />
        <Stat label="Revenue (all time)" value={inr(overview.revenue_total_paise)} testid="admin-stat-total-revenue" />
        <Stat label="Paid subscribers" value={overview.active_paid_subs} testid="admin-stat-paid-subs" />
        <Stat label="Payments" value={overview.payments_count} testid="admin-stat-payments" />
      </div>
      {overview.memorials_pending > 0 && (
        <button onClick={() => goTo("submissions")} className="w-full text-left rounded-xl border px-4 py-3 text-sm hover:bg-amber-50 transition" style={{ borderColor: "#F59E0B", background: "#FFFBEB" }} data-testid="admin-pending-alert">
          <Flower2 className="w-4 h-4 inline mr-2" style={{ color: "#B45309" }} />
          <strong>{overview.memorials_pending}</strong> memorial{overview.memorials_pending > 1 ? "s" : ""} waiting for review — click to open the queue
        </button>
      )}
    </div>
  );
}

function RevenueTab() {
  const [data, setData] = useState(null);
  useEffect(() => { http.get("/admin/console/revenue").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="wofy-card text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  return (
    <div className="space-y-4" data-testid="admin-revenue-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(data.plan_counts || {}).map(([k, v]) => (
          <Stat key={k} label={`${k} subscribers`} value={v} testid={`admin-plan-count-${k}`} />
        ))}
      </div>
      {(data.promo_stats || []).length > 0 && (
        <div className="wofy-card p-0 overflow-x-auto" data-testid="admin-promo-analytics">
          <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 px-4 pt-4 pb-2">Promo code performance</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] font-mono uppercase opacity-60 border-b" style={{ borderColor: "var(--wofy-border)" }}>
              <th className="px-4 py-2">Code</th><th className="px-4 py-2">Sign-ups</th><th className="px-4 py-2">Revenue</th>
              <th className="px-4 py-2">Discount given</th><th className="px-4 py-2">Free activations</th></tr></thead>
            <tbody>
              {data.promo_stats.map((p) => (
                <tr key={p.code} className="border-b last:border-0" style={{ borderColor: "var(--wofy-border)" }} data-testid={`admin-promo-stat-${p.code}`}>
                  <td className="px-4 py-2 font-mono font-semibold">{p.code}</td>
                  <td className="px-4 py-2">{p.uses}</td>
                  <td className="px-4 py-2 font-semibold">{inr(p.revenue_paise)}</td>
                  <td className="px-4 py-2" style={{ color: "#B45309" }}>{inr(p.discount_paise)}</td>
                  <td className="px-4 py-2">{p.free_activations}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="wofy-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] font-mono uppercase opacity-60 border-b" style={{ borderColor: "var(--wofy-border)" }}>
            <th className="px-4 py-3">Month</th><th className="px-4 py-3">Revenue</th><th className="px-4 py-3">Payments</th></tr></thead>
          <tbody>
            {(data.monthly || []).map((m) => (
              <tr key={m.month} className="border-b last:border-0" style={{ borderColor: "var(--wofy-border)" }} data-testid={`admin-revenue-row-${m.month}`}>
                <td className="px-4 py-3">{m.month}</td>
                <td className="px-4 py-3 font-semibold">{inr(m.revenue_paise)}</td>
                <td className="px-4 py-3">{m.payments}</td>
              </tr>
            ))}
            {!(data.monthly || []).length && <tr><td className="px-4 py-6 opacity-60" colSpan={3}>No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContentTab() {
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="admin-content-tab">
      {CONTENT_LINKS.map((l) => (
        <Link key={l.to} to={l.to} className="wofy-card hover:shadow-md transition group" data-testid={`admin-content-link-${l.to.replace(/\//g, "-")}`}>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-sm">{l.label}</div>
            <ExternalLink className="w-4 h-4 opacity-40 group-hover:opacity-80" />
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>{l.desc}</p>
        </Link>
      ))}
    </div>
  );
}

const inrFull = (paise) => (paise == null ? "—" : `₹${(paise / 100).toLocaleString("en-IN")}`);

function SettingsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { http.get("/admin/console/settings").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="wofy-card text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const Row = ({ label, value, ok }) => (
    <div className="flex justify-between items-center py-2 border-b last:border-0 text-sm" style={{ borderColor: "var(--wofy-border)" }}>
      <span className="opacity-70">{label}</span>
      <span className={`font-semibold ${ok === false ? "text-red-600" : ok ? "text-green-700" : ""}`}>{value}</span>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="admin-settings-tab">
      <div className="wofy-card">
        <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-2">Payments</div>
        <Row label="Provider" value={data.payment_provider} />
        <Row label="Razorpay mode" value={data.razorpay_mode} ok={data.razorpay_mode === "live"} />
        <Row label="Razorpay key" value={data.razorpay_key_id_masked || "not set"} />
        <Row label="Webhook secret" value={data.razorpay_webhook_secret_set ? "configured" : "missing"} ok={data.razorpay_webhook_secret_set} />
        {data.razorpay_mode === "test" && (
          <p className="text-xs mt-3 rounded-lg px-3 py-2" style={{ background: "#FFFBEB", color: "#92400E" }} data-testid="admin-settings-testmode-note">
            Razorpay is in TEST mode — no real money is charged. Add live keys to production secrets when ready to go live.
          </p>
        )}
      </div>
      <div className="wofy-card">
        <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-2">Super admins</div>
        <div className="flex flex-wrap gap-1.5">
          {(data.super_admin_emails || []).map((e) => (
            <span key={e} className="rounded-full px-2.5 py-1 text-xs font-mono" style={{ background: "#F1F5F9" }} data-testid={`admin-settings-email-${e}`}>{e}</span>
          ))}
        </div>
        <p className="text-xs opacity-60 mt-2">Managed via SUPER_ADMIN_EMAILS in backend environment secrets.</p>
      </div>
      <div className="wofy-card p-0 overflow-x-auto">
        <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 px-4 pt-4 pb-2">Plan catalogue</div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] font-mono uppercase opacity-60 border-b" style={{ borderColor: "var(--wofy-border)" }}>
            <th className="px-4 py-2">Plan</th><th className="px-4 py-2">Members</th><th className="px-4 py-2">Base (yearly)</th><th className="px-4 py-2">GST 18%</th><th className="px-4 py-2">Total</th></tr></thead>
          <tbody>
            {(data.plans || []).map((p) => (
              <tr key={p.key} className="border-b last:border-0" style={{ borderColor: "var(--wofy-border)" }} data-testid={`admin-settings-plan-${p.key}`}>
                <td className="px-4 py-2 font-semibold">{p.name}</td>
                <td className="px-4 py-2">{p.node_limit ?? "Unlimited"}</td>
                <td className="px-4 py-2">{inrFull(p.price_paise)}</td>
                <td className="px-4 py-2">{p.price_paise ? inrFull(Math.round(p.price_paise * 0.18)) : "—"}</td>
                <td className="px-4 py-2">{p.price_paise ? inrFull(Math.round(p.price_paise * 1.18)) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
